import React, { useState, useEffect } from 'react';
import {
  Sliders,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle2,
  ShieldAlert,
  Info,
  RotateCcw,
} from 'lucide-react';
import { adminApi } from '../services/api.js';
import type { AdminSettingsDto } from '@nova-mine/shared';

export const SettingsView: React.FC = () => {
  const [settings, setSettings] = useState<AdminSettingsDto | null>(null);
  const [formData, setFormData] = useState<AdminSettingsDto | null>(null);
  const [reason, setReason] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await adminApi.getSettings();
      setSettings(res);
      setFormData(res);
    } catch (err: unknown) {
      console.error('[Load Settings Error]', err);
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleChange = (key: keyof AdminSettingsDto, value: any) => {
    if (!formData) return;
    setFormData({
      ...formData,
      [key]: value,
    });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData) return;

    try {
      setIsSaving(true);
      setError(null);
      setSuccessMsg(null);

      const res = await adminApi.updateSettings({
        ...formData,
        reason: reason.trim() || 'Admin configuration update',
      });

      setSuccessMsg('Settings updated and persisted authoritatively in app_settings.');
      setSettings(formData);
      setReason('');
    } catch (err: unknown) {
      console.error('[Save Settings Error]', err);
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading || !formData) {
    return (
      <div className="py-20 text-center glass-panel rounded-3xl border border-white/10">
        <Loader2 className="w-8 h-8 animate-spin text-purple-400 mx-auto mb-2" />
        <p className="text-xs text-gray-400">Loading system parameters...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Policy Card Banner */}
      <div className="glass-panel p-5 rounded-3xl border border-amber-500/30 bg-amber-500/5 flex items-start space-x-3.5">
        <ShieldAlert className="w-5 h-5 text-nova-gold flex-shrink-0 mt-0.5" />
        <div className="text-xs text-amber-200">
          <span className="font-bold text-white uppercase tracking-wider block mb-0.5">
            Authority & Application Policy:
          </span>
          <p className="leading-relaxed">
            By default, changes to mining yield rates apply to <strong className="text-white">future activations and subsequent claim calculations</strong>. Existing active miner instances maintain their recorded <code className="text-nova-gold font-mono">daily_rate</code> stored at activation time.
          </p>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-2xl bg-red-500/10 border border-red-500/30 flex items-center space-x-2 text-xs text-red-400">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {successMsg && (
        <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center space-x-2 text-xs text-emerald-400">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          <span>{successMsg}</span>
        </div>
      )}

      <form onSubmit={handleSave} className="glass-panel rounded-3xl border border-white/10 p-6 space-y-6">
        <div className="flex items-center justify-between border-b border-white/5 pb-4">
          <div>
            <h3 className="text-base font-black text-white flex items-center space-x-2">
              <Sliders className="w-5 h-5 text-purple-400" />
              <span>Reward Targets & System Engine</span>
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Authoritative parameters backed by the Worker API and Turso database.
            </p>
          </div>
          <button
            type="button"
            onClick={loadSettings}
            className="p-2 rounded-xl bg-space-850 hover:bg-space-800 text-gray-400 hover:text-white border border-white/10"
            title="Reset to Current Stored Values"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>

        {/* Section 1: Mining Production Rates */}
        <div className="space-y-4">
          <h4 className="text-xs font-bold uppercase tracking-wider text-purple-400">
            1. Mining Yield Accrual Targets (per 24 Hours / 86,400s)
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-300 mb-1">
                Main Miner Rate (NOVA)
              </label>
              <input
                type="number"
                value={formData.main_miner_daily_rate}
                onChange={(e) => handleChange('main_miner_daily_rate', Number(e.target.value))}
                min={1}
                required
                className="w-full bg-space-900 border border-white/15 rounded-xl px-4 py-2.5 text-xs text-white font-mono focus:outline-none focus:border-purple-400"
              />
              <span className="text-[10px] text-gray-500 mt-1 block">Default: 2,000 / 24h</span>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-300 mb-1">
                Auto Miner Rate (NOVA)
              </label>
              <input
                type="number"
                value={formData.auto_miner_daily_rate}
                onChange={(e) => handleChange('auto_miner_daily_rate', Number(e.target.value))}
                min={1}
                required
                className="w-full bg-space-900 border border-white/15 rounded-xl px-4 py-2.5 text-xs text-white font-mono focus:outline-none focus:border-purple-400"
              />
              <span className="text-[10px] text-gray-500 mt-1 block">Default: 2,000 / 24h</span>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-300 mb-1">
                Stars Miner Rate (NOVA / unit)
              </label>
              <input
                type="number"
                value={formData.stars_miner_daily_rate}
                onChange={(e) => handleChange('stars_miner_daily_rate', Number(e.target.value))}
                min={1000}
                required
                className="w-full bg-space-900 border border-white/15 rounded-xl px-4 py-2.5 text-xs text-white font-mono focus:outline-none focus:border-purple-400"
              />
              <span className="text-[10px] text-gray-500 mt-1 block">Default: 100,000 / 24h</span>
            </div>
          </div>
        </div>

        {/* Section 2: Stars Miner Pricing & Referrals */}
        <div className="space-y-4 pt-4 border-t border-white/5">
          <h4 className="text-xs font-bold uppercase tracking-wider text-nova-gold">
            2. Telegram Stars & Referral Incentives
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-300 mb-1">
                Stars Miner Price (Telegram Stars / XTR)
              </label>
              <input
                type="number"
                value={formData.stars_miner_cost_stars}
                onChange={(e) => handleChange('stars_miner_cost_stars', Number(e.target.value))}
                min={1}
                required
                className="w-full bg-space-900 border border-white/15 rounded-xl px-4 py-2.5 text-xs text-white font-mono focus:outline-none focus:border-nova-gold"
              />
              <span className="text-[10px] text-gray-500 mt-1 block">Must be exactly 10 Stars per specification</span>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-300 mb-1">
                Referral Bonus Reward (NOVA)
              </label>
              <input
                type="number"
                value={formData.referral_reward_coins}
                onChange={(e) => handleChange('referral_reward_coins', Number(e.target.value))}
                min={0}
                required
                className="w-full bg-space-900 border border-white/15 rounded-xl px-4 py-2.5 text-xs text-white font-mono focus:outline-none focus:border-nova-gold"
              />
              <span className="text-[10px] text-gray-500 mt-1 block">Awarded to referrer on verified invite</span>
            </div>
          </div>
        </div>

        {/* Section 3: Website Task & Auto Miner Unlock Quest */}
        <div className="space-y-4 pt-4 border-t border-white/5">
          <h4 className="text-xs font-bold uppercase tracking-wider text-cyan-400">
            3. Website Tasks & Dwell Verification
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-300 mb-1">
                Required Tasks for Auto Miner
              </label>
              <input
                type="number"
                value={formData.auto_miner_required_tasks}
                onChange={(e) => handleChange('auto_miner_required_tasks', Number(e.target.value))}
                min={1}
                required
                className="w-full bg-space-900 border border-white/15 rounded-xl px-4 py-2.5 text-xs text-white font-mono focus:outline-none focus:border-cyan-400"
              />
              <span className="text-[10px] text-gray-500 mt-1 block">Default: 3 tasks</span>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-300 mb-1">
                Minimum Dwell Duration (Seconds)
              </label>
              <input
                type="number"
                value={formData.task_min_dwell_seconds}
                onChange={(e) => handleChange('task_min_dwell_seconds', Number(e.target.value))}
                min={1}
                required
                className="w-full bg-space-900 border border-white/15 rounded-xl px-4 py-2.5 text-xs text-white font-mono focus:outline-none focus:border-cyan-400"
              />
              <span className="text-[10px] text-gray-500 mt-1 block">Anti-skip verification time</span>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-300 mb-1">
                Default Website Task URL
              </label>
              <input
                type="url"
                value={formData.task_default_url}
                onChange={(e) => handleChange('task_default_url', e.target.value)}
                required
                className="w-full bg-space-900 border border-white/15 rounded-xl px-4 py-2.5 text-xs text-white font-mono focus:outline-none focus:border-cyan-400"
              />
            </div>
          </div>
        </div>

        {/* Section 4: AdsGram Placements */}
        <div className="space-y-4 pt-4 border-t border-white/5">
          <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-400">
            4. AdsGram Placement Block IDs
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-300 mb-1">
                Rewarded Video Block ID
              </label>
              <input
                type="text"
                value={formData.adsgram_block_rewarded}
                onChange={(e) => handleChange('adsgram_block_rewarded', e.target.value)}
                required
                className="w-full bg-space-900 border border-white/15 rounded-xl px-4 py-2.5 text-xs text-white font-mono focus:outline-none focus:border-indigo-400"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-300 mb-1">
                Rewarded Interstitial Block ID
              </label>
              <input
                type="text"
                value={formData.adsgram_block_interstitial}
                onChange={(e) => handleChange('adsgram_block_interstitial', e.target.value)}
                required
                className="w-full bg-space-900 border border-white/15 rounded-xl px-4 py-2.5 text-xs text-white font-mono focus:outline-none focus:border-indigo-400"
              />
            </div>
          </div>
        </div>

        {/* Section 5: Policy Selection & Audit Reason */}
        <div className="space-y-4 pt-4 border-t border-white/5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-300 mb-1">
                Effective Policy for Yield Updates
              </label>
              <select
                value={formData.settings_effective_policy}
                onChange={(e) => handleChange('settings_effective_policy', e.target.value as any)}
                className="w-full bg-space-900 border border-white/15 rounded-xl px-4 py-2.5 text-xs text-white focus:outline-none focus:border-purple-400"
              >
                <option value="future_activations">Future Activations Only (Recommended)</option>
                <option value="all_miners">All Existing & Future Miners</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-300 mb-1">
                Mandatory Audit Justification
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason for modifying parameters..."
                className="w-full bg-space-900 border border-white/15 rounded-xl px-4 py-2.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-purple-400"
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="pt-4 border-t border-white/5 flex justify-end">
          <button
            type="submit"
            disabled={isSaving}
            className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 via-indigo-600 to-cyan-500 text-white font-bold text-xs shadow-xl shadow-purple-600/25 hover:brightness-110 active:scale-[0.98] transition-all flex items-center space-x-2"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Saving to Database...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Save System Settings</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
